import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { useLang } from "@/i18n";

const STORAGE_KEY = "ma-cookie-consent-session";

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    // Clean up legacy persistent consent so banner shows again for existing visitors
    try { localStorage.removeItem("ma-cookie-consent-v1"); } catch (_) {}
    const timer = setTimeout(() => {
      let dismissedThisSession = false;
      try { dismissedThisSession = !!sessionStorage.getItem(STORAGE_KEY); } catch (_) {}
      if (!dismissedThisSession) setVisible(true);
    }, 700);
    return () => clearTimeout(timer);
  }, []);

  const persist = (value) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ v: value, at: new Date().toISOString() }));
    } catch (_) {}
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cookie-banner"
          className="fixed inset-x-4 bottom-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-md z-[80] bg-background border border-border shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)] p-6 md:p-7"
        >
          <div className="flex items-start gap-4">
            <span className="shrink-0 w-10 h-10 border border-border flex items-center justify-center text-primary">
              <Cookie strokeWidth={1.5} size={18} />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg text-foreground">{t("cookie.title")}</h3>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">
                {t("cookie.body")}{" "}
                <Link to="/datenschutz" onClick={() => setVisible(false)} className="text-primary underline underline-offset-2">
                  {t("cookie.privacy")}
                </Link>.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => persist("accepted")}
                  data-testid="cookie-accept"
                  className="bg-primary text-primary-foreground px-5 py-2.5 text-sm tracking-wide hover:opacity-90 transition-opacity"
                >
                  {t("cookie.accept")}
                </button>
                <Link
                  to="/datenschutz"
                  onClick={() => setVisible(false)}
                  data-testid="cookie-more"
                  className="text-sm text-foreground/70 hover:text-foreground underline underline-offset-2"
                >
                  {t("cookie.more")}
                </Link>
              </div>
            </div>
            <button
              type="button"
              onClick={() => persist("dismissed")}
              data-testid="cookie-close"
              aria-label="Banner schließen"
              className="shrink-0 -mt-1 -mr-1 w-8 h-8 flex items-center justify-center text-foreground/50 hover:text-foreground transition-colors"
            >
              <X strokeWidth={1.5} size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
